import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;
const GBP = (n) => (n == null ? "—" : "£" + Number(n).toLocaleString("en-GB", { maximumFractionDigits: 0 }));

// ---- Shopify App Bridge v4 session-token auth ----
// App Bridge is loaded from Shopify's CDN in index.html <head> at parse time (only when embedded),
// so window.shopify + idToken() become available inside the Shopify iframe. We mint a FRESH token
// on EVERY request — never a cached/URL/initial-load token in the embedded flow.
function appBridgeReady() {
  return !!(window.shopify && typeof window.shopify.idToken === "function");
}

// Wait for App Bridge to finish initialising inside the Shopify iframe before the first request,
// instead of erroring immediately. Resolves false quickly when not embedded (direct/test load).
function waitForAppBridge(timeoutMs = 10000) {
  if (appBridgeReady()) return Promise.resolve(true);
  if (window.top === window.self) return Promise.resolve(false);
  return new Promise((resolve) => {
    const start = Date.now();
    const iv = setInterval(() => {
      if (appBridgeReady()) { clearInterval(iv); resolve(true); }
      else if (Date.now() - start > timeoutMs) { clearInterval(iv); resolve(false); }
    }, 100);
  });
}

async function getSessionToken() {
  if (appBridgeReady()) {
    try { return await window.shopify.idToken(); } catch (e) { /* fall through */ }
  }
  // Only when NOT embedded (opened directly / automated test) do we accept an explicit token param.
  if (window.top === window.self) {
    const p = new URLSearchParams(window.location.search);
    if (p.get("id_token")) return p.get("id_token");
  }
  return null;
}

// Every protected embedded-admin request goes through here. It waits for App Bridge to be ready
// (so the token exists before /context, /metrics, /settings, etc.), and on a 401 (token expired
// between actions) it mints a fresh token via App Bridge and retries once.
async function api(path, opts = {}, _retry = true) {
  if (!appBridgeReady() && window.top !== window.self) await waitForAppBridge();
  const token = await getSessionToken();
  const headers = { "Content-Type": "application/json", ...(opts.headers || {}) };
  if (token) headers.Authorization = "Bearer " + token;
  const res = await fetch(`${API}/api/shopify/admin${path}`, { ...opts, headers });
  if (res.status === 401 && _retry && appBridgeReady()) {
    return api(path, opts, false);
  }
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || res.statusText);
  return res.json();
}

const Card = ({ title, children, testid, action }) => (
  <div className="bg-white rounded-xl border border-[#e1e3e5] shadow-[0_1px_0_rgba(22,29,37,0.05)] p-5" data-testid={testid}>
    {title && (
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-[15px] font-semibold text-[#1a1c1d]">{title}</h2>
        {action}
      </div>
    )}
    {children}
  </div>
);

const Stat = ({ label, value, testid, accent }) => (
  <div className="bg-white rounded-xl border border-[#e1e3e5] p-5" data-testid={testid}>
    <div className="text-[13px] text-[#6d7175] font-medium">{label}</div>
    <div className={`text-[26px] font-bold mt-1 ${accent ? "text-[#008060]" : "text-[#1a1c1d]"}`}>{value}</div>
  </div>
);

const Field = ({ label, children }) => (
  <label className="block mb-4">
    <span className="block text-[13px] font-medium text-[#1a1c1d] mb-1.5">{label}</span>
    {children}
  </label>
);

const inputCls = "w-full rounded-lg border border-[#8a8a8a] px-3 py-2 text-[14px] text-[#1a1c1d] focus:outline-none focus:border-[#005bd3] focus:ring-2 focus:ring-[#005bd3]/30";

export default function ShopifyAdmin() {
  const [tab, setTab] = useState("dashboard");
  const [ctx, setCtx] = useState(null);
  const [metrics, setMetrics] = useState(null);
  const [settings, setSettings] = useState(null);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const boot = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const cfg = await fetch(`${API}/api/shopify/config`).then((r) => r.json());
      setPlans(cfg.plans || []);
      const embedded = window.top !== window.self;
      // App Bridge is injected in index.html <head> at parse time when embedded. Wait until it's
      // ready so a fresh session token is available before the first protected request.
      const ready = await waitForAppBridge();
      if (embedded && !ready) {
        throw new Error("Shopify App Bridge did not initialise — please refresh the app from Shopify Admin.");
      }
      // Shopify App Pricing returns the merchant here with ?plan_handle=<plan> after hosted approval.
      const planHandle = new URLSearchParams(window.location.search).get("plan_handle");
      const c = await api(planHandle ? `/context?plan_handle=${encodeURIComponent(planHandle)}` : "/context");
      setCtx(c);
      if (planHandle && c.live_subscription?.active) toast.success(`Subscribed to ${c.plan?.replace("_", " ")} plan`);
      const [m, s] = await Promise.all([api("/metrics"), api("/settings")]);
      setMetrics(m); setSettings(s);
    } catch (e) {
      setError(e.message || "Could not authenticate with Shopify.");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { boot(); }, [boot]);

  const saveSettings = async (partial) => {
    setSaving(true);
    try {
      await api("/settings", { method: "PATCH", body: JSON.stringify(partial) });
      toast.success("Settings saved");
      const s = await api("/settings"); setSettings(s);
    } catch (e) { toast.error(e.message || "Save failed"); }
    finally { setSaving(false); }
  };

  const subscribe = async (plan) => {
    try {
      const r = await api("/subscribe", { method: "POST", body: JSON.stringify({ plan }) });
      if (r.confirmation_url) {
        if (window.top) window.top.location.href = r.confirmation_url;
        else window.location.href = r.confirmation_url;
      }
    } catch (e) { toast.error(e.message || "Could not start subscription"); }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f1f1f1]">
        <div className="text-[#008060] font-semibold tracking-wide animate-pulse" data-testid="shopify-admin-loading">Loading AI Turf Designer…</div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f1f1f1] px-6">
        <div className="max-w-md text-center bg-white rounded-xl border border-[#e1e3e5] p-8" data-testid="shopify-admin-error">
          <div className="text-[#d72c0d] font-semibold mb-2">Unable to load admin</div>
          <p className="text-[#6d7175] text-sm">{error}</p>
          <p className="text-[#6d7175] text-xs mt-4">Open this app from inside your Shopify admin, or ensure the app is installed and configured.</p>
          <button onClick={boot} className="mt-5 bg-[#1a1c1d] text-white text-sm font-semibold px-5 py-2 rounded-lg" data-testid="shopify-admin-retry">Retry</button>
        </div>
      </div>
    );
  }

  const TABS = [
    ["dashboard", "Dashboard"], ["designer", "Designer settings"],
    ["pricing", "Pricing"], ["billing", "Plan & billing"],
  ];

  return (
    <div className="min-h-screen bg-[#f1f1f1] text-[#1a1c1d]" style={{ fontFamily: "-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif" }} data-testid="shopify-admin">
      <div className="max-w-[1000px] mx-auto px-5 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-5">
          <div>
            <h1 className="text-[20px] font-bold">AI Turf Designer</h1>
            <p className="text-[13px] text-[#6d7175]">{ctx?.brand_name} · {ctx?.shop}</p>
          </div>
          <span className={`text-[12px] font-semibold px-3 py-1 rounded-full ${ctx?.live_subscription?.active ? "bg-[#e3f1df] text-[#0c5132]" : "bg-[#fde8e8] text-[#8e1f0b]"}`} data-testid="shopify-admin-plan-badge">
            {ctx?.live_subscription?.active && ctx?.plan ? `${ctx.plan.replace("_", " ").toUpperCase()} · ${ctx.status}` : "NO ACTIVE PLAN"}
          </span>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 border-b border-[#e1e3e5] mb-6">
          {TABS.map(([k, label]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`shopify-tab-${k}`}
              className={`px-4 py-2.5 text-[14px] font-medium border-b-2 -mb-px transition-colors ${tab === k ? "border-[#008060] text-[#008060]" : "border-transparent text-[#6d7175] hover:text-[#1a1c1d]"}`}>
              {label}
            </button>
          ))}
        </div>

        {tab === "dashboard" && <Dashboard metrics={metrics} ctx={ctx} />}
        {tab === "designer" && <DesignerSettings settings={settings} onSave={saveSettings} saving={saving} ctx={ctx} />}
        {tab === "pricing" && <PricingSettings settings={settings} onSave={saveSettings} saving={saving} />}
        {tab === "billing" && <Billing plans={plans} ctx={ctx} onSubscribe={subscribe} />}

        <div className="mt-8 pt-5 border-t border-[#e1e3e5] text-[12px] text-[#6d7175] flex flex-wrap items-center gap-x-2">
          <span>AI Turf Designer by Prime Gym Turf LTD.</span>
          <a href="https://aiturfdesigner.com/privacy-policy" target="_blank" rel="noopener noreferrer" className="text-[#005bd3] font-medium hover:underline" data-testid="shopify-privacy-link">Privacy Policy</a>
          <a href="https://aiturfdesigner.com/terms" target="_blank" rel="noopener noreferrer" className="text-[#005bd3] font-medium hover:underline" data-testid="shopify-terms-link">Terms of Service</a>
        </div>
      </div>
    </div>
  );
}

function Dashboard({ metrics, ctx }) {
  const m = metrics || {};
  const link = ctx?.tenant_link || {};
  const editorUrl = ctx?.theme_editor_url || (ctx?.shop ? `https://${ctx.shop}/admin/themes/current/editor?template=index` : "#");
  return (
    <div className="space-y-5" data-testid="shopify-dashboard">
      <Card title="Add the designer to your store" testid="shopify-onboarding">
        <div className="flex items-center gap-2 text-[13px] mb-3" data-testid="onboarding-link-status">
          <span className={`inline-block w-2 h-2 rounded-full ${link.ok ? "bg-[#008060]" : "bg-[#d72c0d]"}`} />
          {link.ok
            ? <span className="text-[#4a4a4a]">Your store is linked to <b>{ctx?.brand_name || ctx?.tenant_slug}</b> automatically — no setup codes needed.</span>
            : <span className="text-[#d72c0d]">Store link pending{link.error ? ` (${link.error})` : ""}. Refresh this page to retry.</span>}
        </div>
        <ol className="text-[13px] text-[#4a4a4a] space-y-2 list-decimal ml-4 mb-4">
          <li>Click the button below — Shopify's theme editor opens with the <b>AI Turf Designer</b> block ready to place.</li>
          <li>Position it on the page, pick <b>button</b> or <b>inline</b> display, then click <b>Save</b>.</li>
        </ol>
        <a href={editorUrl} target="_top" rel="noopener" data-testid="onboarding-open-editor"
          className="inline-block bg-[#008060] text-white text-sm font-semibold px-5 py-2.5 rounded-lg hover:bg-[#006e52]">
          Add AI Turf Designer to my store →
        </a>
      </Card>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <Stat label="Pipeline value" value={GBP(m.pipeline_value)} testid="metric-pipeline" accent />
        <Stat label="Total leads" value={m.leads ?? 0} testid="metric-leads" />
        <Stat label="Won projects" value={GBP(m.won_value)} testid="metric-won" />
        <Stat label="Avg project value" value={GBP(m.avg_project_value)} testid="metric-avg" />
        <Stat label="Conversion rate" value={m.conversion_rate != null ? m.conversion_rate + "%" : "—"} testid="metric-conversion" />
        <Stat label="Designs created" value={m.designs_created ?? 0} testid="metric-designs" />
      </div>

      <Card title="AI generations" testid="shopify-ai-usage">
        <div className="text-[14px] text-[#6d7175]">
          {ctx?.ai?.unlimited ? "Unlimited" : (
            <span><b className="text-[#1a1c1d]">{ctx?.ai?.remaining ?? 0}</b> of {ctx?.ai?.limit ?? 0} monthly generations remaining</span>
          )}
        </div>
        {!ctx?.ai?.active && <div className="text-[13px] text-[#b98900] mt-2">Subscribe to a plan to unlock AI turf generation.</div>}
      </Card>

      <Card title="Recent enquiries" testid="shopify-recent-leads">
        {(m.recent_leads || []).length === 0 ? (
          <p className="text-[14px] text-[#6d7175]">No enquiries yet. Add the “Design Your Turf” block to a product page to start capturing leads.</p>
        ) : (
          <table className="w-full text-[13px]">
            <thead><tr className="text-[#6d7175] text-left border-b border-[#e1e3e5]">
              <th className="py-2">Customer</th><th>Size</th><th>Value</th><th>Status</th>
            </tr></thead>
            <tbody>
              {m.recent_leads.map((l, i) => (
                <tr key={i} className="border-b border-[#f1f1f1]" data-testid={`shopify-lead-row-${i}`}>
                  <td className="py-2">{l.name}<div className="text-[#6d7175]">{l.email}</div></td>
                  <td>{l.dimensions?.length}m × {l.dimensions?.width}m</td>
                  <td>{GBP(l.estimated_price)}</td>
                  <td><span className="px-2 py-0.5 rounded-full bg-[#f1f1f1] text-[#6d7175]">{l.status}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
}

function DesignerSettings({ settings, onSave, saving, ctx }) {
  const [f, setF] = useState(() => ({
    name: settings?.branding?.name || "",
    logo_url: settings?.branding?.logo_url || "",
    primary: settings?.branding?.brand_colours?.primary || "#00D26A",
    secondary: settings?.branding?.brand_colours?.secondary || "#161616",
    accent: settings?.branding?.brand_colours?.accent || "#ffffff",
    ai_style: settings?.designer?.ai_style || "",
    default_length: settings?.designer?.default_length || "",
    default_width: settings?.designer?.default_width || "",
  }));
  const upd = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const save = () => onSave({
    branding: { name: f.name, logo_url: f.logo_url, brand_colours: { primary: f.primary, secondary: f.secondary, accent: f.accent } },
    designer: { ai_style: f.ai_style, default_length: f.default_length || null, default_width: f.default_width || null },
  });
  return (
    <div className="space-y-5" data-testid="shopify-designer-settings">
      <Card title="Brand identity">
        <Field label="Company name"><input className={inputCls} value={f.name} onChange={(e) => upd("name", e.target.value)} data-testid="settings-name" /></Field>
        <Field label="Logo URL"><input className={inputCls} value={f.logo_url} onChange={(e) => upd("logo_url", e.target.value)} placeholder="https://…/logo.png" data-testid="settings-logo" /></Field>
        <div className="grid grid-cols-3 gap-4">
          {[["primary", "Brand colour"], ["secondary", "Marking colour"], ["accent", "Accent colour"]].map(([k, label]) => (
            <Field key={k} label={label}>
              <div className="flex items-center gap-2">
                <input type="color" value={f[k]} onChange={(e) => upd(k, e.target.value)} className="h-9 w-10 rounded border border-[#8a8a8a]" data-testid={`settings-colour-${k}`} />
                <input className={inputCls} value={f[k]} onChange={(e) => upd(k, e.target.value)} />
              </div>
            </Field>
          ))}
        </div>
      </Card>
      <Card title="Designer defaults">
        <Field label="AI design style (guides the AI look, e.g. ‘clean professional, bold sprint lanes’)">
          <textarea rows={3} className={inputCls} value={f.ai_style} onChange={(e) => upd("ai_style", e.target.value)} data-testid="settings-ai-style" />
        </Field>
        <div className="grid grid-cols-2 gap-4">
          <Field label={`Default length (m)${ctx?.plan ? "" : ""}`}><input type="number" step="0.5" className={inputCls} value={f.default_length} onChange={(e) => upd("default_length", e.target.value)} data-testid="settings-default-length" /></Field>
          <Field label="Default width (m)"><input type="number" step="0.5" className={inputCls} value={f.default_width} onChange={(e) => upd("default_width", e.target.value)} data-testid="settings-default-width" /></Field>
        </div>
      </Card>
      <div className="flex justify-end">
        <button onClick={save} disabled={saving} className="bg-[#1a1c1d] text-white text-sm font-semibold px-6 py-2.5 rounded-lg disabled:opacity-50" data-testid="settings-save">
          {saving ? "Saving…" : "Save settings"}
        </button>
      </div>
    </div>
  );
}

function PricingSettings({ settings, onSave, saving }) {
  const p = settings?.pricing || {};
  const [f, setF] = useState(() => ({
    standard_per_sqm: p.standard_per_sqm ?? "",
    standard_base: p.standard_base ?? "",
    express_per_sqm: p.express_per_sqm ?? "",
    express_base: p.express_base ?? "",
    min_price: p.min_price ?? "",
    std_time: settings?.turnaround?.standard_turnaround ?? "",
    exp_enabled: settings?.turnaround?.express_enabled !== false,
    exp_time: settings?.turnaround?.express_turnaround ?? "",
    exp_max_w: settings?.turnaround?.express_max_width ?? "",
  }));
  const upd = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const num = (v) => (v === "" ? null : Number(v));
  const save = () => onSave({ pricing: {
    standard_per_sqm: num(f.standard_per_sqm), standard_base: num(f.standard_base),
    express_per_sqm: num(f.express_per_sqm), express_base: num(f.express_base), min_price: num(f.min_price),
  }, turnaround: {
    standard_enabled: true, standard_turnaround: f.std_time,
    express_enabled: f.exp_enabled, express_turnaround: f.exp_time,
    express_max_width: f.exp_max_w === "" ? null : Number(f.exp_max_w),
  } });
  return (
    <div className="space-y-5" data-testid="shopify-pricing-settings">
      <Card title="Standard pricing">
        <div className="grid grid-cols-2 gap-4">
          <Field label="Price per m² (£)"><input type="number" className={inputCls} value={f.standard_per_sqm} onChange={(e) => upd("standard_per_sqm", e.target.value)} data-testid="pricing-standard-sqm" /></Field>
          <Field label="Base price (£)"><input type="number" className={inputCls} value={f.standard_base} onChange={(e) => upd("standard_base", e.target.value)} data-testid="pricing-standard-base" /></Field>
        </div>
      </Card>
      <Card title="Express pricing">
        <div className="grid grid-cols-2 gap-4">
          <Field label="Price per m² (£)"><input type="number" className={inputCls} value={f.express_per_sqm} onChange={(e) => upd("express_per_sqm", e.target.value)} data-testid="pricing-express-sqm" /></Field>
          <Field label="Base price (£)"><input type="number" className={inputCls} value={f.express_base} onChange={(e) => upd("express_base", e.target.value)} data-testid="pricing-express-base" /></Field>
        </div>
      </Card>
      <Card title="Minimum project value">
        <Field label="Minimum project price (£) — quotes never fall below this"><input type="number" className={inputCls} value={f.min_price} onChange={(e) => upd("min_price", e.target.value)} data-testid="pricing-min" /></Field>
      </Card>
      <Card title="Turnaround times (shown to customers before checkout)">
        <Field label="Standard turnaround wording"><input className={inputCls} value={f.std_time} onChange={(e) => upd("std_time", e.target.value)} data-testid="turnaround-standard" placeholder="e.g. 4–6 weeks" /></Field>
        <label className="flex items-center gap-2 text-sm text-white/80 mt-3"><input type="checkbox" checked={f.exp_enabled} onChange={(e) => upd("exp_enabled", e.target.checked)} data-testid="turnaround-express-enabled" /> Offer Express turnaround</label>
        {f.exp_enabled && (
          <div className="grid grid-cols-2 gap-4 mt-3">
            <Field label="Express turnaround wording"><input className={inputCls} value={f.exp_time} onChange={(e) => upd("exp_time", e.target.value)} data-testid="turnaround-express" placeholder="e.g. 2–3 weeks" /></Field>
            <Field label="Express max width (m) — blank = no limit"><input type="number" className={inputCls} value={f.exp_max_w} onChange={(e) => upd("exp_max_w", e.target.value)} data-testid="turnaround-express-maxw" placeholder="no limit" /></Field>
          </div>
        )}
      </Card>
      <div className="flex justify-end">
        <button onClick={save} disabled={saving} className="bg-[#1a1c1d] text-white text-sm font-semibold px-6 py-2.5 rounded-lg disabled:opacity-50" data-testid="pricing-save">
          {saving ? "Saving…" : "Save pricing"}
        </button>
      </div>
    </div>
  );
}

export function Billing({ plans, ctx, onSubscribe }) {
  const live = ctx?.live_subscription || {};
  const endDate = live.current_period_end ? new Date(live.current_period_end).toLocaleDateString() : null;
  return (
    <div className="space-y-5" data-testid="shopify-billing">
      {!live.active && (
        <div className="rounded-xl border border-[#f1b6ad] bg-[#fff4f2] p-5" data-testid="shopify-subscription-ended-banner">
          <div className="text-[15px] font-bold text-[#8e1f0b]">{live.ended ? "Your subscription has ended" : "No active subscription"}</div>
          <p className="text-[13px] text-[#4a4a4a] mt-1">
            {live.ended ? "Your previous plan was cancelled, so AI generation and premium features are paused." : "Choose a plan to activate AI Turf Designer for your store."} Pick a plan below to resubscribe on Shopify's secure billing page.
          </p>
          <button onClick={() => onSubscribe(null)} data-testid="shopify-resubscribe-btn"
            className="mt-3 text-sm font-semibold px-4 py-2.5 rounded-lg bg-[#1a1c1d] text-white hover:bg-black">Choose a plan on Shopify →</button>
        </div>
      )}
      {live.active && live.cancel_at_end_of_cycle && (
        <div className="rounded-xl border border-[#ffd79d] bg-[#fff8e8] p-4 text-[13px] text-[#5c4300]" data-testid="shopify-subscription-cancelling-banner">
          Your plan is scheduled to cancel{endDate ? ` on ${endDate}` : " at the end of this billing cycle"}. You keep full access until then; pick a plan below to stay subscribed.
        </div>
      )}
      <p className="text-[14px] text-[#6d7175]">Choose a plan below to continue on Shopify's secure billing page, where you review and approve the subscription. Free trial included on every plan; trial length varies by plan. All plans are billed through Shopify.</p>
      <div className="grid md:grid-cols-2 gap-4">
        {plans.map((pl) => {
          const current = live.active && ctx?.plan === pl.key;
          return (
            <div key={pl.key} className={`bg-white rounded-xl border p-5 ${pl.highlight ? "border-[#008060] ring-1 ring-[#008060]" : "border-[#e1e3e5]"}`} data-testid={`shopify-plan-${pl.key}`}>
              <div className="flex items-baseline justify-between">
                <h3 className="text-[16px] font-bold">{pl.name}</h3>
                <div className="text-[18px] font-bold">${pl.price}<span className="text-[13px] font-normal text-[#6d7175]">/mo</span></div>
              </div>
              <ul className="mt-3 space-y-1.5 text-[13px] text-[#4a4a4a]">
                {pl.features.map((ft, i) => <li key={i} className="flex gap-2"><span className="text-[#008060]">✓</span>{ft}</li>)}
              </ul>
              <button disabled={current} onClick={() => onSubscribe(pl.key)} data-testid={`shopify-subscribe-${pl.key}`}
                className={`mt-4 w-full text-sm font-semibold px-4 py-2.5 rounded-lg ${current ? "bg-[#e3f1df] text-[#0c5132] cursor-default" : "bg-[#1a1c1d] text-white hover:bg-black"}`}>
                {current ? "Current plan" : `Select ${pl.name} on Shopify →`}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
